<template>
  <Teleport to="body">
    <div v-if="visible" class="user-message-dialog">
    <div class="dialog-overlay" @click="handleClose"></div>
    <div class="dialog-content">
      <div class="container">
        <div class="dialog-body">
          <!-- 左侧 -->
          <div class="left-panel">
            <button class="type-btn" @click="backToSubmit">留言/建议</button>

            <div class="history-list">
              <div v-if="historyLoading" class="loading-tip">加载中...</div>
              <div v-else-if="historyList.length === 0" class="empty-tip">暂无记录</div>
              <div
                v-for="item in historyList"
                :key="item.id"
                class="history-item"
                :class="{ active: selectedId === item.id }"
                @click="selectHistory(item)"
              >
                <i class="fas fa-comment-alt history-icon"></i>
                <span class="history-title">{{ item.content.substring(0, 10) }}{{ item.content.length > 10 ? '...' : '' }}</span>
                <span class="history-state" :class="item.state === 1 ? 'state-pending' : 'state-done'">
                  {{ item.state === 1 ? $t('manga.userMessage.pending') : $t('manga.userMessage.replied') }}
                </span>
              </div>
            </div>
          </div>

          <!-- 分割线 -->
          <div class="divider"></div>

          <!-- 右侧 -->
          <div class="right-panel">
            <!-- 提交表单 -->
            <div v-if="!selectedId" class="submit-form">
              <textarea
                v-model="form.content"
                :placeholder="$t('manga.userMessage.placeholder')"
                rows="10"
                maxlength="500"
              ></textarea>
              <div class="submit-bar">
                <div class="type-selector">
                  <span class="type-option" :class="{ active: form.type === 1 }" @click="form.type = 1">留言</span>
                  <span class="type-divider">/</span>
                  <span class="type-option" :class="{ active: form.type === 2 }" @click="form.type = 2">建议</span>
                </div>
                <button class="btn-submit" :disabled="!form.content.trim() || submitting" @click="handleSubmit">
                  {{ submitting ? $t('manga.userMessage.submitting') : $t('manga.userMessage.submit') }}
                </button>
              </div>
            </div>

            <!-- 详情展示 -->
            <div v-else class="detail-view">
              <div class="detail-content">
                <div class="detail-content-label">{{ selectedItem.type === 1 ? $t('manga.userMessage.messageContent') : $t('manga.userMessage.suggestionContent') }}</div>
                {{ selectedItem.content }}
              </div>
              <div class="detail-reply" v-if="selectedItem.replyContent">
                <div class="reply-label">官方回复</div>
                {{ selectedItem.replyContent }}
              </div>
              <div class="detail-meta">
                <span class="type-tag" :class="selectedItem.type === 1 ? 'type-message' : 'type-suggest'">
                  {{ selectedItem.type === 1 ? $t('manga.userMessage.message') : $t('manga.userMessage.suggestion') }}
                </span>
                <span class="detail-status" :class="selectedItem.state === 1 ? 'status-pending' : 'status-replied'">
                  {{ selectedItem.state === 1 ? $t('manga.userMessage.pending') : $t('manga.userMessage.replied') }}
                </span>
                <span class="detail-time">{{ selectedItem.createTime }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { addUserMessage, getUserMessageList } from '@/api/manga/userMessage.js'

const { t } = useI18n()

const props = defineProps({
  visible: Boolean
})

const emit = defineEmits(['update:visible', 'success'])

const form = ref({
  type: 1,
  content: ''
})

const submitting = ref(false)
const historyLoading = ref(false)
const historyList = ref([])
const selectedId = ref(null)
const selectedItem = ref({})

watch(() => props.visible, (val) => {
  if (val) {
    form.value = { type: 1, content: '' }
    selectedId.value = null
    selectedItem.value = {}
    loadHistory()
  }
})

const loadHistory = async () => {
  historyLoading.value = true
  try {
    const result = await getUserMessageList({ pageNo: 1, pageSize: 20 })
    if (result.data && result.data.records) {
      historyList.value = result.data.records
    } else if (Array.isArray(result.data)) {
      historyList.value = result.data
    }
  } catch (error) {
    console.error('加载历史记录失败:', error)
  } finally {
    historyLoading.value = false
  }
}

const selectHistory = (item) => {
  selectedId.value = item.id
  selectedItem.value = item
}

const backToSubmit = () => {
  selectedId.value = null
  selectedItem.value = {}
}

const handleClose = () => {
  emit('update:visible', false)
}

const handleSubmit = async () => {
  if (!form.value.content.trim() || submitting.value) return
  submitting.value = true
  try {
    await addUserMessage(form.value)
    form.value.content = ''
    loadHistory()
    emit('success')
  } catch (error) {
    console.error('提交失败:', error)
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.user-message-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.dialog-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(6px);
}

.dialog-content {
  position: relative;
  max-width: 1100px;
  max-height: 75vh;
  width: 100%;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(20px);
}

.container {
  padding: 24px 24px 16px;
  height: 72vh;
  overflow-y: auto;
}

.container::-webkit-scrollbar {
  width: 6px;
}

.container::-webkit-scrollbar-track {
  background: rgba(30, 41, 59, 0.3);
  border-radius: 3px;
}

.container::-webkit-scrollbar-thumb {
  background: rgba(94, 234, 212, 0.4);
  border-radius: 3px;
}

.container::-webkit-scrollbar-thumb:hover {
  background: rgba(94, 234, 212, 0.6);
}

.container {
  scrollbar-width: thin;
  scrollbar-color: rgba(94, 234, 212, 0.4) rgba(30, 41, 59, 0.3);
}

/* 主体布局 */
.dialog-body {
  display: flex;
  gap: 0;
  min-height: 400px;
  height: calc(72vh - 40px);
}

/* 左侧 */
.left-panel {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.type-btn {
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid rgba(94, 234, 212, 0.3);
  background: rgba(94, 234, 212, 0.1);
  color: #5eead4;
  transition: all 0.3s;
  margin-bottom: 16px;
}

.type-btn:hover {
  background: rgba(94, 234, 212, 0.2);
  border-color: rgba(94, 234, 212, 0.5);
}

.history-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.empty-tip {
  text-align: center;
  color: #64748b;
  font-size: 13px;
  padding: 40px 0;
}

.loading-tip {
  text-align: center;
  color: #5eead4;
  font-size: 13px;
  padding: 40px 0;
}

.history-item {
  padding: 10px 12px;
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  background: rgba(30, 41, 59, 0.3);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.history-item:hover {
  border-color: rgba(94, 234, 212, 0.3);
  background: rgba(30, 41, 59, 0.5);
}

.history-item.active {
  border-color: #5eead4;
  background: rgba(94, 234, 212, 0.1);
}

.history-icon {
  color: #64748b;
  font-size: 12px;
  margin-right: 8px;
  flex-shrink: 0;
}

.history-item:hover .history-icon,
.history-item.active .history-icon {
  color: #5eead4;
}

.history-title {
  font-size: 13px;
  color: #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.history-state {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

/* 分割线 */
.divider {
  width: 1px;
  background: rgba(94, 234, 212, 0.15);
  margin: 0 20px;
  flex-shrink: 0;
}

/* 右侧 */
.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.submit-form,
.detail-view {
  flex: 1;
  display: flex;
  flex-direction: column;
}

textarea {
  flex: 1;
  width: 100%;
  padding: 14px;
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 8px;
  background: rgba(30, 41, 59, 0.4);
  color: #e2e8f0;
  font-size: 14px;
  resize: none;
  outline: none;
  box-sizing: border-box;
}

textarea:focus {
  border-color: #5eead4;
  background: rgba(30, 41, 59, 0.6);
}

textarea::placeholder {
  color: #64748b;
}

.submit-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
}

.type-selector {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
}

.type-option {
  cursor: pointer;
  color: #64748b;
  transition: color 0.2s;
}

.type-option:hover {
  color: #94a3b8;
}

.type-option.active {
  color: #5eead4;
  font-weight: 600;
}

.type-divider {
  color: #475569;
}

.btn-submit {
  padding: 10px 32px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: linear-gradient(90deg, #5eead4, #a78bfa);
  color: white;
  transition: all 0.3s;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(94, 234, 212, 0.3);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 详情样式 */
.detail-content {
  flex: 1;
  font-size: 14px;
  color: #e2e8f0;
  line-height: 1.7;
  background: rgba(30, 41, 59, 0.4);
  padding: 14px;
  border-radius: 8px;
  border: 1px solid rgba(94, 234, 212, 0.15);
}

.detail-reply {
  margin-top: 12px;
  font-size: 14px;
  color: #5eead4;
  line-height: 1.7;
  background: rgba(94, 234, 212, 0.08);
  padding: 18px 20px;
  border-radius: 8px;
  border: 1px solid rgba(94, 234, 212, 0.2);
  flex: 1;
}

.reply-label {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 6px;
}

.detail-content-label {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 6px;
}

.detail-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(94, 234, 212, 0.1);
}

.type-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.type-message {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.15);
}

.type-suggest {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.15);
}

.state-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.state-pending {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.15);
}

.state-done {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.15);
}

.detail-time {
  font-size: 12px;
  color: #64748b;
  margin-left: auto;
}
</style>
